import { VueQueryPlugin } from '@tanstack/vue-query'
import { createHead } from '@unhead/vue/client'
import { createPinia } from 'pinia'
import piniaPluginPersistedstate from 'pinia-plugin-persistedstate'
import { createApp } from 'vue'
import 'uno.css'
import './design/tokens/_base.scss'

import App from './app/App.vue'
import { dismissBootScreen } from './app/boot-screen'
import { queryClient, queryClientConfig } from './app/query-client'
import { i18n } from './locales'
import { installVersionSkewDetection } from './modules/rollout/composables/useVersionSkew'
import { router } from './router'
import { configureAuth } from './shared/api/client'
import { initErrorTracking, tracker } from './shared/observability/error-tracking'
import { initTelemetry, observeWebVitals } from './shared/observability/telemetry'
import { useAuthStore } from './stores/auth'
import { useUiStore } from './stores/ui'

async function bootstrap() {
  if (import.meta.env.MODE === 'mocking') {
    const { worker } = await import('./mocks/browser')
    await worker.start({ onUnhandledRequest: 'bypass' })
  }

  const pinia = createPinia()
  pinia.use(piniaPluginPersistedstate)

  // `@unhead/vue` drives the marketplace's SEO: the API hands back a complete `seo` payload
  // per listing and `useSeo` renders it, JSON-LD included (backend phase-08 §3.8).
  const head = createHead()

  const app = createApp(App)
    .use(pinia)
    .use(router)
    .use(i18n)
    .use(head)
    .use(VueQueryPlugin, queryClientConfig)

  // The HTTP client needs the session, and the session store needs the client. Wiring them
  // here — rather than importing one from the other — keeps the dependency one-way and the
  // access token out of every module that happens to make a request.
  const auth = useAuthStore(pinia)
  configureAuth({
    getAccessToken: () => auth.accessToken,
    refresh: () => auth.refresh(),
    onUnauthenticated: () => {
      const current = router.currentRoute.value
      auth.reset()
      queryClient.clear()
      // `matched.length === 0` is vue-router's START_LOCATION: nothing has been navigated to
      // yet, so there is no meaningful place to come back to. Let the guard decide instead.
      if (current.matched.length > 0 && !current.meta.public) {
        useUiStore(pinia).notify('info', i18n.global.t('auth.sessionExpired'))
        router.push({ path: '/auth/login', query: { next: current.fullPath } })
      }
    },
  })

  auth.watchVisibility()

  // Phase 14 §2.3. Every V2 response carries `X-Api-Version`; this watches for the API moving
  // under an open tab. Installed once, as an interceptor on requests that are happening anyway,
  // so it costs nothing of its own and there is no polling.
  installVersionSkewDetection()

  // Phase 14 §2.4. Product telemetry. **Off unless `VITE_ANALYTICS_ENDPOINT` is set and the
  // viewer has not asked not to be tracked** — the honest default for a module whose whole
  // purpose is sending data somewhere. The event set is closed and documented in
  // `docs/v2/telemetry.md`.
  initTelemetry()

  // Real-user LCP, CLS and INP per route, so the Phase 13 budgets are checked against reality
  // rather than only against a Lighthouse run on a developer's machine. Reported through the
  // same sink; when telemetry is off, `track` is a no-op and this observes for nothing, so it
  // is only installed when there is somewhere for the numbers to go.
  observeWebVitals(
    (metric) => tracker().captureMessage(`web-vital:${metric.name}`, { ...metric }),
    () => router.currentRoute.value.path,
  )

  // Phase 13. Anything a route boundary does not catch -- a guard, a plugin, a watcher -- is
  // reported through the same scrubbed reporter. Never re-thrown: the app keeps running.
  app.config.errorHandler = (error, _instance, info) => {
    tracker().capture(error, { info, route: router.currentRoute.value.fullPath })
  }
  void initErrorTracking(app, router)

  // `App.vue` takes the boot screen down when the first layout renders. If the first navigation
  // fails instead (a chunk that will not load), nothing will render, and a spinner turning
  // forever would claim otherwise.
  router.isReady().catch(() => dismissBootScreen())

  app.mount('#app')
}

bootstrap()
